import { Clock, Data, Effect, Result } from 'effect';

const DEFAULT_TTL_SECONDS = 60 * 60;
const CLOCK_SKEW_SECONDS = 60;

export class DocumentTokenError extends Data.TaggedError('DocumentTokenError')<{
  readonly method: 'signDocumentAgentToken' | 'verifyDocumentAgentToken';
  readonly status?: number;
  readonly message: string;
}> {}

const documentTokenErrorCauses = new WeakMap<DocumentTokenError, unknown>();

function makeDocumentTokenError(
  method: DocumentTokenError['method'],
  cause: unknown,
): DocumentTokenError {
  const error = new DocumentTokenError({ method, message: 'Document token operation failed' });
  documentTokenErrorCauses.set(error, cause);
  return error;
}

export function documentTokenErrorCause(error: DocumentTokenError): unknown {
  return documentTokenErrorCauses.get(error);
}

async function runTokenBoundary<A>(effect: Effect.Effect<A, DocumentTokenError>): Promise<A> {
  const result = await Effect.runPromise(Effect.result(effect));
  if (Result.isSuccess(result)) return result.success;
  throw documentTokenErrorCause(result.failure);
}

export interface DocumentAgentTokenParams {
  docId: string;
  ttlSeconds?: number;
  nowSeconds?: number;
}

export interface DocumentAgentTokenVerification {
  valid: boolean;
  docId?: string;
  exp?: number;
  error?: string;
}

function assertDocumentId(docId: string): string {
  const normalized = docId.trim();
  if (!normalized) {
    throw new Error('docId is required');
  }
  if (normalized.includes('.')) {
    throw new Error('docId must not contain "."');
  }
  return normalized;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function timingSafeEqual(a: string, b: string): boolean {
  const enc = new TextEncoder();
  const left = enc.encode(a);
  const right = enc.encode(b);
  if (left.length !== right.length) return false;
  let diff = 0;
  for (let i = 0; i < left.length; i += 1) {
    diff |= left[i] ^ right[i];
  }
  return diff === 0;
}

function hmacSha256Base64UrlEffect(
  signingKey: string,
  payload: string,
  method: DocumentTokenError['method'],
) {
  const failure = (cause: unknown) => makeDocumentTokenError(method, cause);
  return Effect.gen(function* () {
    if (!signingKey) return yield* Effect.fail(failure(new Error('signingKey is required')));
    const key = yield* Effect.tryPromise({
      try: () =>
        crypto.subtle.importKey(
          'raw',
          new TextEncoder().encode(signingKey),
          { name: 'HMAC', hash: 'SHA-256' },
          false,
          ['sign'],
        ),
      catch: failure,
    });
    const sig = yield* Effect.tryPromise({
      try: () => crypto.subtle.sign('HMAC', key, new TextEncoder().encode(payload)),
      catch: failure,
    });
    return bytesToBase64Url(new Uint8Array(sig));
  });
}

export function signDocumentAgentToken(
  signingKey: string,
  params: DocumentAgentTokenParams,
): Promise<string> {
  return runTokenBoundary(signDocumentAgentTokenEffect(signingKey, params));
}

export const signDocumentAgentTokenEffect = Effect.fn('signDocumentAgentTokenEffect')(function* (
  signingKey: string,
  params: DocumentAgentTokenParams,
) {
  const failure = (cause: unknown) => makeDocumentTokenError('signDocumentAgentToken', cause);
  const docId = yield* Effect.try({ try: () => assertDocumentId(params.docId), catch: failure });
  const ttlSeconds = params.ttlSeconds ?? DEFAULT_TTL_SECONDS;
  if (!Number.isFinite(ttlSeconds) || ttlSeconds <= 0) {
    return yield* Effect.fail(failure(new Error('ttlSeconds must be a positive number')));
  }
  const now = params.nowSeconds ?? Math.floor((yield* Clock.currentTimeMillis) / 1000);
  const exp = now + Math.floor(ttlSeconds);
  const payload = `${docId}.${exp}`;
  const sig = yield* hmacSha256Base64UrlEffect(signingKey, payload, 'signDocumentAgentToken');
  return `${payload}.${sig}`;
});

export function verifyDocumentAgentToken(
  signingKeys: string | Array<string | undefined | null>,
  token: string | null | undefined,
  expectedDocId: string,
  opts: { nowSeconds?: number } = {},
): Promise<DocumentAgentTokenVerification> {
  return runTokenBoundary(verifyDocumentAgentTokenEffect(signingKeys, token, expectedDocId, opts));
}

export const verifyDocumentAgentTokenEffect = Effect.fn('verifyDocumentAgentTokenEffect')(
  function* (
    signingKeys: string | Array<string | undefined | null>,
    token: string | null | undefined,
    expectedDocId: string,
    opts: { nowSeconds?: number } = {},
  ) {
    if (!token) return { valid: false, error: 'Missing token' };

    const parts = token.split('.');
    if (parts.length !== 3) return { valid: false, error: 'Malformed token' };

    const [docId, expRaw, sig] = parts;
    if (!docId || !expRaw || !sig) return { valid: false, error: 'Missing token fields' };
    if (docId !== expectedDocId) return { valid: false, error: 'Token document mismatch' };

    const exp = Number.parseInt(expRaw, 10);
    if (!Number.isFinite(exp)) return { valid: false, error: 'Invalid expiry' };
    if (
      (opts.nowSeconds ?? Math.floor((yield* Clock.currentTimeMillis) / 1000)) >
      exp + CLOCK_SKEW_SECONDS
    ) {
      return { valid: false, error: 'Token expired' };
    }

    const keys = Array.isArray(signingKeys) ? signingKeys : [signingKeys];
    const payload = `${docId}.${exp}`;
    for (const key of keys) {
      if (!key) continue;
      const expectedSig = yield* hmacSha256Base64UrlEffect(
        key,
        payload,
        'verifyDocumentAgentToken',
      );
      if (timingSafeEqual(sig, expectedSig)) {
        return { valid: true, docId, exp };
      }
    }

    return { valid: false, error: 'Invalid signature' };
  },
);
